import {
	Button,
	Group,
	Select,
	Stack,
	Stepper,
	Tabs,
	TextInput,
	Textarea,
	NumberInput,
	Switch,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useState, useEffect, useRef, useMemo } from 'react';
import {
	useGetAllWorkoutCategoriesQuery,
	useGetSupportedLanguagesQuery,
} from '@codezo/graphql';
import { ExerciseBuilder } from './ExerciseBuilder';

interface WorkoutFormProps {
	initialValues?: any;
	onSubmit: (values: any) => void;
	onCancel: () => void;
	loading?: boolean;
}

interface ExerciseFormData {
	translations: Array<{
		language: string;
		name: string;
		description: string;
		tips: string[];
	}>;
	sortOrder: number;
	durationMin?: number;
	durationMax?: number;
	typeData: any;
}

interface TranslationData {
	name: string;
	why: string;
	about: string;
	instructions: string;
	thankYouMessage: string;
	reflectionMessage: string;
}

interface WorkoutFormData {
	categoryId: string;
	type: string;
	intensity: string;
	frequency: number;
	frequencyPeriod: string;
	durationMin?: number;
	durationMax?: number;
	isPremium: boolean;
	translations: Record<string, TranslationData>; // Dynamic language codes
	exercises: ExerciseFormData[];
}

const DEFAULT_TRANSLATION = {
	name: '',
	why: '',
	about: '',
	instructions: '',
	thankYouMessage: '',
	reflectionMessage: '',
};

export function WorkoutForm({ initialValues, onSubmit, onCancel, loading }: WorkoutFormProps) {
	const [activeStep, setActiveStep] = useState(0);
	const [activeTab, setActiveTab] = useState<string>('en');
	const uploadedFileIdsRef = useRef<Set<string>>(new Set());

	// Fetch supported languages from backend
	const { data: languagesData } = useGetSupportedLanguagesQuery();

	// Fetch workout categories
	const { data: categoriesData, loading: categoriesLoading } = useGetAllWorkoutCategoriesQuery();

	// Derive languages and labels from query
	const LANGUAGES = useMemo(
		() => languagesData?.getSupportedLanguages?.map((lang) => lang.code) || ['en', 'da'],
		[languagesData]
	);

	const LANGUAGE_LABELS = useMemo(
		() =>
			languagesData?.getSupportedLanguages?.reduce(
				(acc, lang) => ({
					...acc,
					[lang.code]: lang.name,
				}),
				{} as Record<string, string>
			) || { en: 'English', da: 'Danish' },
		[languagesData]
	);

	// Transform initialValues from GraphQL format to form format (dynamically based on LANGUAGES)
	const transformedInitialValues = useMemo(() => {
		// Build translations object dynamically
		const buildTranslations = (source?: any[]) => {
			const translations: any = {};
			LANGUAGES.forEach((lang) => {
				translations[lang] =
					source?.find((t: any) => t.language === lang) || DEFAULT_TRANSLATION;
			});
			return translations;
		};
		return initialValues
			? {
				categoryId: initialValues.categoryId || '',
				type: initialValues.type || 'REFLECTION_INPUT',
				intensity: initialValues.intensity || 'CASUAL',
				frequency: initialValues.frequency || 7,
				frequencyPeriod: initialValues.frequencyPeriod || 'DAILY',
				durationMin: initialValues.durationMin,
				durationMax: initialValues.durationMax,
				isPremium: initialValues.isPremium || false,
				translations: buildTranslations(initialValues.translations),
				exercises:
					initialValues.exercises?.map((ex: any) => {
						// Transform typeData to ensure inputs have translations
						let typeData = ex.typeData;
						if (ex.typeData?.inputs && Array.isArray(ex.typeData.inputs)) {
							typeData = {
								...ex.typeData,
								inputs: ex.typeData.inputs
									.filter((input: { translations?: unknown[] }) => input.translations && input.translations.length > 0)
									.map((input: { translations?: Array<{ language: string; title?: string; placeholder?: string }> }) => ({
										translations: LANGUAGES.map(lang => {
											const existingTrans = input.translations?.find((t: { language: string }) => t.language === lang);
											return existingTrans || {
												language: lang,
												title: '',
												placeholder: ''
											};
										})
									}))
							};
						}

						return {
							sortOrder: ex.sortOrder,
							groupId: ex.groupId !== undefined ? ex.groupId : null,
							durationMin: ex.durationMin,
							durationMax: ex.durationMax,
							typeData: typeData,
							translations: ex.translations || [],
						};
					}) || [],
			}
		: {
				categoryId: '',
				type: 'REFLECTION_INPUT',
				intensity: 'CASUAL',
				frequency: 7,
				frequencyPeriod: 'WEEKLY',
				durationMin: undefined,
				durationMax: undefined,
				isPremium: false,
				translations: buildTranslations(),
				exercises: [],
			};
	}, [initialValues, LANGUAGES]);

	const form = useForm({
		initialValues: transformedInitialValues,
		validate: {
			categoryId: (value) => (!value ? 'Category is required' : null),
			frequency: (value) => (value < 1 ? 'Frequency must be at least 1' : null),
			// @ts-expect-error - Complex nested validation returns objects for nested fields
			translations: (translations) => {
				// Validate that if a field is filled in one language, it must be filled in all
				const fields = ['name', 'why', 'about', 'instructions', 'thankYouMessage', 'reflectionMessage'] as const;
				const errors: Record<string, any> = {};

				fields.forEach(field => {
					const values = LANGUAGES.map(lang => translations[lang]?.[field]?.trim());
					const hasAnyValue = values.some(v => v);
					const hasMissingValue = values.some(v => !v);

					if (hasAnyValue && hasMissingValue) {
						// Find which languages are missing
						const missingLangs = LANGUAGES.filter((lang, i) => !values[i])
							.map(lang => LANGUAGE_LABELS[lang])
							.join(', ');

						LANGUAGES.forEach((lang, i) => {
							if (!values[i]) {
								if (!errors[lang]) errors[lang] = {};
								errors[lang][field] = `Must be filled in all languages (missing: ${missingLangs})`;
							}
						});
					}

					// English name is always required
					if (field === 'name' && !translations['en']?.[field]?.trim()) {
						if (!errors['en']) errors['en'] = {};
						errors['en'][field] = 'English name is required';
					}
				});

				return Object.keys(errors).length > 0 ? errors : null;
			},
			exercises: (exercises) => {
				if (!exercises || !Array.isArray(exercises)) {
					return null;
				}

				const exerciseErrors: any[] = [];

				exercises.forEach((exercise: any, exIdx: number) => {
					const errors: any = {};

					// Validate exercise translations (name, description)
					const fields = ['name', 'description'] as const;
					fields.forEach(field => {
						const values = exercise.translations.map((t: any) => t[field]?.trim());
						const hasAnyValue = values.some((v: any) => v);
						const hasMissingValue = values.some((v: any) => !v);

						if (hasAnyValue && hasMissingValue) {
							const missingLangs = exercise.translations
								.filter((t: any, i: number) => !values[i])
								.map((t: any) => LANGUAGE_LABELS[t.language])
								.join(', ');

							if (!errors.translations) errors.translations = [];
							exercise.translations.forEach((trans: any, tIdx: number) => {
								if (!values[tIdx]) {
									if (!errors.translations[tIdx]) errors.translations[tIdx] = {};
									errors.translations[tIdx][field] = `Must be filled in all languages (missing: ${missingLangs})`;
								}
							});
						}
					});

					// Validate tips - all languages must have same number of tips, all filled
					const tipsByLang = exercise.translations.map((t: any) => t.tips || []);
					const hasAnyTips = tipsByLang.some((tips: any) => tips.some((t: any) => t?.trim()));

					if (hasAnyTips) {
						const maxTips = Math.max(...tipsByLang.map((tips: any) => tips.length));

						exercise.translations.forEach((trans: any, tIdx: number) => {
							const tips = tipsByLang[tIdx];

							// Check if this language has fewer tips than others
							if (tips.length < maxTips) {
								if (!errors.translations) errors.translations = [];
								if (!errors.translations[tIdx]) errors.translations[tIdx] = {};
								errors.translations[tIdx].tips = `Missing ${maxTips - tips.length} tip(s) compared to other languages`;
							}

							// Check if any tip is empty
							tips.forEach((tip: any, tipIdx: number) => {
								if (!tip?.trim()) {
									if (!errors.translations) errors.translations = [];
									if (!errors.translations[tIdx]) errors.translations[tIdx] = {};
									if (!errors.translations[tIdx].tips) errors.translations[tIdx].tips = [];
									errors.translations[tIdx].tips[tipIdx] = 'Tip cannot be empty';
								}
							});
						});
					}

					// Validate reflection input translations
					if (exercise.typeData?.inputs) {
						exercise.typeData.inputs.forEach((input: any, inputIdx: number) => {
							if (!input.translations) return;

							const inputFields = ['title', 'placeholder'] as const;
							inputFields.forEach(field => {
								const values = input.translations.map((t: any) => t[field]?.trim());
								const hasAnyValue = values.some((v: any) => v);
								const hasMissingValue = values.some((v: any) => !v);

								if (hasAnyValue && hasMissingValue) {
									const missingLangs = input.translations
										.filter((t: any, i: number) => !values[i])
										.map((t: any) => LANGUAGE_LABELS[t.language])
										.join(', ');

									if (!errors.typeData) errors.typeData = {};
									if (!errors.typeData.inputs) errors.typeData.inputs = [];
									if (!errors.typeData.inputs[inputIdx]) errors.typeData.inputs[inputIdx] = { translations: [] };

									input.translations.forEach((trans: any, tIdx: number) => {
										if (!values[tIdx]) {
											if (!errors.typeData.inputs[inputIdx].translations[tIdx]) {
												errors.typeData.inputs[inputIdx].translations[tIdx] = {};
											}
											errors.typeData.inputs[inputIdx].translations[tIdx][field] =
												`Must be filled in all languages (missing: ${missingLangs})`;
										}
									});
								}
							});
						});
					}

					exerciseErrors[exIdx] = Object.keys(errors).length > 0 ? errors : null;
				});

				return exerciseErrors.some(e => e !== null) ? exerciseErrors : null;
			}
		},
	});

	// Reset form when initialValues prop changes (e.g., when switching from create to edit)
	// Use a ref to track the previous initialValues to avoid resetting on every render
	const prevInitialValuesRef = useRef(initialValues);
	useEffect(() => {
		// Only reset if initialValues actually changed (new workout opened)
		if (prevInitialValuesRef.current !== initialValues) {
			form.setValues(transformedInitialValues);
			form.resetDirty();
			prevInitialValuesRef.current = initialValues;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [initialValues, transformedInitialValues]);

	// Track uploaded file IDs for cleanup
	useEffect(() => {
		// Collect all uploaded file IDs from exercises
		const fileIds = form.values.exercises
			.map((ex: any) => ex.typeData?.fileId)
			.filter((id: string) => id);

		fileIds.forEach((id: string) => uploadedFileIdsRef.current.add(id));
	}, [form.values.exercises]);

	// Cleanup on unmount or cancel
	useEffect(() => {
		return () => {
			// On unmount, clean up any uploaded files that weren't submitted
			const fileIdsToCleanup = Array.from(uploadedFileIdsRef.current);
			fileIdsToCleanup.forEach(async (fileId) => {
				// Find if this fileId is still in the form
				const stillInUse = form.values.exercises.some(
					(ex: any) => ex.typeData?.fileId === fileId
				);
				if (!stillInUse) {
					// This file was uploaded but removed, clean it up
					// Note: We need the blobUrl to delete, but we don't have it here
					// The MediaUploader handles immediate cleanup on remove
				}
			});
		};
	}, []);

	const handleSubmit = (values: WorkoutFormData) => {
		// Transform form data to match GraphQL input format
		// Strip __typename from translations (added by GraphQL queries)
		const translations = LANGUAGES.map((lang) => {
			const { __typename, ...translation } = values.translations[lang] as any;
			return {
				language: lang,
				...translation,
			};
		});

		const exercises = values.exercises.map((exercise, index) => {
			// Remove internal tracking fields and __typename before sending to backend
			const { _uploadedFileId, __typename, ...exerciseData } = exercise as any;

			// Clean translations in exercises
			const cleanedTranslations = exerciseData.translations?.map((trans: any) => {
				const { __typename: _typename, ...cleanTranslation } = trans;
				return cleanTranslation;
			});

			return {
				...exerciseData,
				translations: cleanedTranslations,
				sortOrder: index,
			};
		});

		onSubmit({
			categoryId: values.categoryId,
			type: values.type,
			intensity: values.intensity,
			frequency: values.frequency,
			frequencyPeriod: values.frequencyPeriod,
			durationMin: values.durationMin,
			durationMax: values.durationMax,
			isPremium: values.isPremium,
			translations,
			exercises,
		});

		// Clear tracked file IDs after successful submission
		uploadedFileIdsRef.current.clear();
	};

	const handleCancel = async () => {
		// Note: Individual file cleanup is handled by MediaUploader's onRemove
		// This is just for tracking purposes
		uploadedFileIdsRef.current.clear();
		onCancel();
	};

	const nextStep = () => {
		// Validate current step before moving forward
		if (activeStep === 0) {
			// Only validate step 1 fields (categoryId, frequency, translations)
			// Don't validate exercises yet - that happens on final submit
			const step1Errors = form.validateField('categoryId');
			const frequencyErrors = form.validateField('frequency');
			const translationErrors = form.validateField('translations');

			if (step1Errors.hasError || frequencyErrors.hasError || translationErrors.hasError) {
				return;
			}
		}
		setActiveStep((current) => (current < 1 ? current + 1 : current));
	};

	const prevStep = () => setActiveStep((current) => (current > 0 ? current - 1 : current));

	return (
		<form
			onSubmit={form.onSubmit(
				handleSubmit,
				(validationErrors) => {
					// Show notification when validation fails
					console.error('Validation errors:', validationErrors);

					notifications.show({
						title: 'Validation Error',
						message: 'Please check all fields marked in red. Make sure to fill in all required translations.',
						color: 'red',
						autoClose: 5000,
					});
				}
			)}
			style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}
		>
			{/* Stepper - Fixed at top */}
			<div style={{ padding: '1.5rem 1.5rem', display: 'flex', alignItems: 'center' }}>
				<Stepper active={activeStep} onStepClick={setActiveStep} style={{ width: '100%' }}>
					<Stepper.Step label="Workout Details" description="Basic information" />
					<Stepper.Step label="Exercises" description="Add exercises" />
				</Stepper>
			</div>

			{/* Scrollable content */}
			<div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.5rem 1.5rem' }}>
				{/* Step 1: Workout Details */}
				{activeStep === 0 && (
					<Stack gap="md">
						{/* Basic Fields */}
						<Select
							label="Category"
							placeholder="Select category"
							required
							data={
								categoriesData?.getAllWorkoutCategories
									.filter((cat) => cat.isActive)
									.map((cat) => ({
										value: cat.id,
										label: cat.name,
									})) || []
							}
							disabled={categoriesLoading}
							{...form.getInputProps('categoryId')}
						/>

						<Select
							label="Exercise Type"
							placeholder="Select the type of exercises in this workout"
							required
							description="All exercises in this workout will use this type"
							data={[
								{ value: 'REFLECTION_INPUT', label: 'Reflection Input' },
								{ value: 'AUDIO', label: 'Audio' },
								{ value: 'VIDEO', label: 'Video' },
								{ value: 'BREATHING', label: 'Breathing' },
								{ value: 'IMAGE_UPLOAD', label: 'Image Upload' },
							]}
							{...form.getInputProps('type')}
							onChange={(value) => {
								if (!value) return;
								form.setFieldValue('type', value);
								// Reset all exercises' typeData when workout type changes
								const exercises = form.values.exercises.map((ex: any) => {
									if (value === 'REFLECTION_INPUT') {
										return {
											...ex,
											typeData: { inputs: [{ title: '', placeholder: '' }] },
										};
									} else if (value === 'AUDIO' || value === 'VIDEO') {
										return { ...ex, typeData: { url: '', duration: 0 } };
									} else if (value === 'BREATHING') {
										return {
											...ex,
											typeData: { inhale: 4, hold: 4, exhale: 6, rounds: 10 },
										};
									} else if (value === 'IMAGE_UPLOAD') {
										return {
											...ex,
											typeData: { minImages: 1, maxImages: 3 },
										};
									}
									return ex;
								});
								form.setFieldValue('exercises', exercises);
							}}
						/>

						<Select
							label="Intensity"
							required
							data={[
								{ value: 'CASUAL', label: 'Casual' },
								{ value: 'MODERATE', label: 'Moderate' },
								{ value: 'INTENSIVE', label: 'Intensive' },
							]}
							{...form.getInputProps('intensity')}
						/>

						<Group grow>
							<NumberInput
								label="Frequency"
								required
								min={1}
								max={99}
								{...form.getInputProps('frequency')}
							/>
							<Select
								label="Frequency Period"
								required
								data={[
									{ value: 'DAILY', label: 'Per Day' },
									{ value: 'WEEKLY', label: 'Per Week' },
									{ value: 'MONTHLY', label: 'Per Month' },
									{ value: 'YEARLY', label: 'Per Year' },
								]}
								{...form.getInputProps('frequencyPeriod')}
							/>
						</Group>

						<Group grow>
							<NumberInput
								label="Min Duration (minutes)"
								placeholder="Optional"
								min={1}
								description="Overall workout duration minimum"
								{...form.getInputProps('durationMin')}
							/>
							<NumberInput
								label="Max Duration (minutes)"
								placeholder="Optional"
								min={1}
								description="Overall workout duration maximum"
								{...form.getInputProps('durationMax')}
							/>
						</Group>

						<Switch
							label="Premium Access Required"
							description="Only users with premium access can view this workout"
							{...form.getInputProps('isPremium', { type: 'checkbox' })}
						/>

						{/* Translation Tabs */}
						<Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'en')}>
							<Tabs.List>
								{LANGUAGES.map((lang) => {
									// Check if this language has any translation errors
									const translationErrors = form.errors?.translations as any;
									const hasErrors = translationErrors && translationErrors[lang] &&
										Object.keys(translationErrors[lang]).length > 0;

									return (
										<Tabs.Tab
											key={lang}
											value={lang}
											color={hasErrors ? 'red' : undefined}
											style={{ color: hasErrors ? 'var(--mantine-color-red-6)' : undefined }}
										>
											{LANGUAGE_LABELS[lang]}
										</Tabs.Tab>
									);
								})}
							</Tabs.List>

							{LANGUAGES.map((lang) => {
								// Helper to get error for a specific field
								const getFieldError = (field: string) => {
									const translationErrors = form.errors?.translations as any;
									return translationErrors?.[lang]?.[field] || '';
								};

								return (
									<Tabs.Panel key={lang} value={lang} pt="md">
										<Stack gap="md">
											<TextInput
												label="Name"
												placeholder={`Workout name in ${LANGUAGE_LABELS[lang]}`}
												required={lang === 'en'}
												value={form.values.translations[lang]?.name || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.name`, e.target.value)}
												error={getFieldError('name')}
											/>

											<Textarea
												label="Why"
												placeholder="Why should users do this workout?"
												rows={3}
												value={form.values.translations[lang]?.why || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.why`, e.target.value)}
												error={getFieldError('why')}
											/>

											<Textarea
												label="About"
												placeholder="What is this workout about?"
												rows={3}
												value={form.values.translations[lang]?.about || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.about`, e.target.value)}
												error={getFieldError('about')}
											/>

											<Textarea
												label="Instructions"
												placeholder="Step-by-step instructions"
												rows={4}
												value={form.values.translations[lang]?.instructions || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.instructions`, e.target.value)}
												error={getFieldError('instructions')}
											/>

											{/* Commented out - Thank You Messages now handled via translation system */}
											{/* <Textarea
												label="Thank You Message"
												placeholder="Message shown after completion"
												rows={2}
												value={form.values.translations[lang]?.thankYouMessage || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.thankYouMessage`, e.target.value)}
												error={getFieldError('thankYouMessage')}
											/> */}

											<Textarea
												label="Reflection Message"
												placeholder="Prompt for reflection"
												rows={2}
												value={form.values.translations[lang]?.reflectionMessage || ''}
												onChange={(e) => form.setFieldValue(`translations.${lang}.reflectionMessage`, e.target.value)}
												error={getFieldError('reflectionMessage')}
											/>
										</Stack>
									</Tabs.Panel>
								);
							})}
						</Tabs>
					</Stack>
				)}

				{/* Step 2: Exercises */}
				{activeStep === 1 && <ExerciseBuilder form={form} languages={LANGUAGES} languageLabels={LANGUAGE_LABELS} />}
			</div>

			{/* Navigation Buttons - Fixed at bottom */}
			<Group
				justify="space-between"
				style={{ borderTop: '1px solid #dee2e6', padding: '1rem 1.5rem' }}
			>
				<Button variant="subtle" onClick={handleCancel} disabled={loading}>
					Cancel
				</Button>

				<Group>
					{activeStep > 0 && (
						<Button variant="default" onClick={prevStep}>
							Back
						</Button>
					)}
					{activeStep < 1 && <Button onClick={nextStep}>Next Step</Button>}
					{activeStep === 1 && (
						<Button type="submit" loading={loading}>
							{initialValues ? 'Update Workout' : 'Create Workout'}
						</Button>
					)}
				</Group>
			</Group>
		</form>
	);
}
